import {
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import { RefObject } from 'react';

import { Domain } from '@superfunnel/types/domain';
import { DynamicPage } from '@superfunnel/types/dynamic-page';

import { AnalyticsRange } from '../contexts/DashboardContext';

export const emptyConverter = <
  T extends DocumentData,
>(): FirestoreDataConverter<T, T> => {
  return {
    toFirestore: (v) => v as any,
    fromFirestore: (v) => v.data() as any,
  };
};

// export const PREVIEW_ROOT = 'https://preview_.superfunnel.page';
// export const PREVIEW_ROOT = 'https://superfunnel.page';
const devSitesUrl = import.meta.env.VITE_SITES_URL || 'http://localhost:4000';
export const PREVIEW_ROOT = import.meta.env.DEV
  ? `http://preview_.localhost:${new URL(devSitesUrl).port}`
  : 'https://superfunnel.page';

export function buildPreviewUrl(
  domain: string,
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  websiteId: string,
  token: string,
  editable: boolean,
) {
  return `${PREVIEW_ROOT}?site=${domain}&websiteId=${websiteId}&accountId=${accountId}&campaignId=${dynamicPageId}&variantId=${variantId}&token=${token}${editable ? `&editable=${editable}` : ''}`;
}

export const STEPPER_PREVIEW_URL = `${PREVIEW_ROOT}/stepper`;

export function sendMessageToIframe(
  message: any,
  ref?: RefObject<HTMLIFrameElement | null>,
) {
  const cleanMessage = JSON.stringify(message);

  if (ref?.current) {
    ref.current.contentWindow?.postMessage(cleanMessage, PREVIEW_ROOT);
    return true;
  }

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

export function sendMessageToIframeByWebsiteId(
  message: any,
  websiteId: string,
) {
  const cleanMessage = JSON.stringify(message);

  const iframes = document.getElementsByClassName('iframe-website-preview');

  for (const iframeElem of iframes) {
    const iframeWebsiteId =
      iframeElem.attributes.getNamedItem('data-website-id')?.value;
    if (!iframeWebsiteId) continue;
    if (iframeWebsiteId !== websiteId) continue;
    (iframeElem as HTMLIFrameElement).contentWindow?.postMessage(
      cleanMessage,
      PREVIEW_ROOT,
    );
  }

  return true;
}

export function dateToString(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Parses a `YYYY-MM-DD` string as a local date. `new Date('YYYY-MM-DD')` parses
 * it as UTC midnight, which resolves to the previous day in negative offsets.
 */
export function parseDateString(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function analyticsRangeToDates(analyticsRange: AnalyticsRange): {
  startDate: string;
  endDate: string;
} {
  if (!analyticsRange) {
    throw new Error('analyticsRange is required');
  }

  if (typeof analyticsRange === 'string') {
    switch (analyticsRange) {
      case 'today': {
        const startDate = new Date();
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last7Days': {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 6);
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      case 'last30Days': {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 29);
        const endDate = new Date();
        return {
          startDate: dateToString(startDate),
          endDate: dateToString(endDate),
        };
      }

      default:
        throw new Error('unsupported date range');
    }
  }

  return {
    startDate: analyticsRange.startDate,
    endDate: analyticsRange.endDate,
  };
}

export function getDynamicPageRelevantDomains(
  domains: QueryDocumentSnapshot<Domain>[],
  dynamicPage: DynamicPage,
): QueryDocumentSnapshot<Domain>[] {
  if (dynamicPage.domains) {
    return domains.filter((d) =>
      dynamicPage.domains?.includes(d.data().domain),
    );
  }

  return domains;
}

const PRIMARY_DOMAIN_TYPE_PRIORITY: Domain['type'][] = [
  'custom',
  'reverse-proxy',
  'subdomain',
];

export function getPrimaryDomain<T extends QueryDocumentSnapshot<Domain>>(
  domains: T[] | undefined,
): T | undefined {
  if (!domains) return;

  for (const type of PRIMARY_DOMAIN_TYPE_PRIORITY) {
    const domain = domains.find((d) => d.data().type === type);
    if (domain) return domain;
  }

  return domains[0];
}

/**
 * Explains why a page has no usable URL. Only approved domains reach the
 * dashboard, so a page pinned to a domain that is still pending resolves to no
 * relevant domains at all — without this the URL and preview render blank.
 */
export function getNoApprovedDomainMessage(
  dynamicPage: DynamicPage | undefined,
  relevantDomains: QueryDocumentSnapshot<Domain>[] | undefined,
): string | undefined {
  if (!dynamicPage || !relevantDomains || relevantDomains.length > 0) return;

  const pinnedDomains = dynamicPage.domains;
  return pinnedDomains?.length
    ? `${pinnedDomains.join(', ')} is not an approved domain yet. Approve it under Settings → Domains, or set this page to All Domains.`
    : 'No approved domains yet. Add and verify a domain under Settings → Domains.';
}
